import React, { Component } from "react";

class VoiceWidget extends Component {
  componentDidMount() {
    // Dynamically inject the widget script
    const script = document.createElement("script");
    // For now it reads from your public folder.
    // In production, change this to your CDN, e.g.:
    // script.src = "https://cdn.caredevo.com/voicebot-widget.js";
    script.src = "/voicebot-widget.js";
    script.async = true;
    script.setAttribute("data-key", "member-caredevo-demo"); // identify this site/account
    document.body.appendChild(script);

    this.voicebotScript = script;
  }

  componentWillUnmount() {
    // Cleanup: remove script + DOM nodes it created
    if (this.voicebotScript && this.voicebotScript.parentNode) {
      this.voicebotScript.parentNode.removeChild(this.voicebotScript);
    }

    const bubble = document.getElementById("voicebot-bubble");
    const panel = document.getElementById("voicebot-panel");
    if (bubble && bubble.parentNode) bubble.parentNode.removeChild(bubble);
    if (panel && panel.parentNode) panel.parentNode.removeChild(panel);

    if (window.__voicebotWidgetLoaded) {
      delete window.__voicebotWidgetLoaded;
    }
  }

  render() {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100">
        {/* Top bar */}
        <header className="border-b border-slate-800">
          <div className="mx-auto max-w-5xl px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-emerald-500/10 border border-emerald-500/40 flex items-center justify-center text-emerald-400 text-xl">
                🎧
              </div>
              <div>
                <div className="text-sm font-semibold tracking-wide uppercase text-slate-400">
                  Caredevo Labs
                </div>
                <div className="text-sm text-slate-400">
                  Voice Assistant Widget
                </div>
              </div>
            </div>
            <div className="hidden sm:flex items-center gap-3 text-xs text-slate-400">
              <span className="px-2 py-1 rounded-full border border-emerald-500/40 text-emerald-300">
                member.caredevo.com
              </span>
              <span className="px-2 py-1 rounded-full border border-slate-700">
                Internal demo
              </span>
            </div>
          </div>
        </header>

        {/* Main content */}
        <main className="mx-auto max-w-5xl px-4 py-10 md:py-12">
          <div className="grid gap-10 md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] items-start">
            {/* Left / main */}
            <section>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
                Let your website visitors{" "}
                <span className="text-emerald-400">talk</span> to Caredevo.
              </h1>
              <p className="text-slate-300 text-sm md:text-base mb-6 max-w-xl">
                This page is a live demo of the Caredevo Voice Assistant widget.
                The round mic button in the bottom-right corner is injected
                entirely by a single{" "}
                <code className="px-1 py-0.5 rounded bg-slate-900 text-xs">
                  &lt;script&gt;
                </code>{" "}
                tag, just like your future customers will use on their own
                sites.
              </p>

              <div className="grid gap-4 md:grid-cols-3 mb-8">
                <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
                  <div className="text-sm font-semibold mb-1">
                    1. Drop-in widget
                  </div>
                  <p className="text-xs text-slate-400">
                    One script tag adds a floating voice assistant to any site:
                    WordPress, custom React, static HTML, anything.
                  </p>
                </div>
                <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
                  <div className="text-sm font-semibold mb-1">
                    2. Voice → AI → Voice
                  </div>
                  <p className="text-xs text-slate-400">
                    In the real version, audio goes to your backend, into STT,
                    GPT, then TTS, and streams the reply back.
                  </p>
                </div>
                <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
                  <div className="text-sm font-semibold mb-1">
                    3. Usage-based SaaS
                  </div>
                  <p className="text-xs text-slate-400">
                    Each{" "}
                    <code className="bg-slate-950 px-1 py-0.5 rounded text-[10px]">
                      data-key
                    </code>{" "}
                    maps to a Caredevo member account for billing & analytics.
                  </p>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4 md:p-5 mb-8">
                <h2 className="text-sm font-semibold mb-3">
                  Script your customers will use
                </h2>
                <p className="text-xs text-slate-400 mb-2">
                  Example snippet you’ll expose in the Caredevo dashboard for
                  each practice:
                </p>
                <pre className="text-xs md:text-sm bg-slate-950 rounded-xl p-3 overflow-x-auto text-slate-100 border border-slate-800">
{`<script
  src="https://cdn.caredevo.com/voicebot-widget.js"
  data-key="CUSTOMER_SPECIFIC_API_KEY">
</script>`}
                </pre>
                <p className="text-[11px] text-slate-500 mt-2">
                  On this internal demo page we’re loading{" "}
                  <code className="bg-slate-950 px-1 py-0.5 rounded">
                    /voicebot-widget.js
                  </code>{" "}
                  from the React public folder and using{" "}
                  <code className="bg-slate-950 px-1 py-0.5 rounded">
                    member-caredevo-demo
                  </code>{" "}
                  as the key.
                </p>
              </div>

              <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4 md:p-5">
                <h2 className="text-sm font-semibold mb-3">
                  How this page works (for your future self)
                </h2>
                <ol className="space-y-2 text-xs text-slate-300">
                  <li>
                    <span className="font-semibold text-slate-100">
                      1. React renders this page
                    </span>{" "}
                    at <code className="bg-slate-950 px-1 py-0.5 rounded">/voice-widget</code> (or whichever route you choose) on{" "}
                    <span className="italic">member.caredevo.com</span>.
                  </li>
                  <li>
                    <span className="font-semibold text-slate-100">
                      2. componentDidMount
                    </span>{" "}
                    injects{" "}
                    <code className="bg-slate-950 px-1 py-0.5 rounded">
                      voicebot-widget.js
                    </code>{" "}
                    into <code className="bg-slate-950 px-1 py-0.5 rounded">document.body</code>.
                  </li>
                  <li>
                    <span className="font-semibold text-slate-100">
                      3. The widget script
                    </span>{" "}
                    reads its <code className="bg-slate-950 px-1 py-0.5 rounded">
                      data-key
                    </code>{" "}
                    and attaches the floating mic bubble + panel UI.
                  </li>
                  <li>
                    <span className="font-semibold text-slate-100">
                      4. In production
                    </span>{" "}
                    it will call your backend (STT → GPT → TTS) and count usage
                    against that key for billing.
                  </li>
                </ol>
              </div>
            </section>

            {/* Right / side card */}
            <aside className="space-y-4">
              <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/5 p-4">
                <h2 className="text-sm font-semibold mb-2 text-emerald-200">
                  Live widget demo
                </h2>
                <p className="text-xs text-emerald-100/80 mb-2">
                  Look at the bottom-right corner of this page: that mic bubble
                  is running off the same script you’ll ship to clinics.
                </p>
                <p className="text-xs text-emerald-100/70">
                  In this version it just runs a fake “recording” and fake AI
                  response. Next step is wiring it to your existing Whisper + GPT
                  + TTS backend.
                </p>
              </div>

              <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
                <h3 className="text-xs font-semibold mb-2 text-slate-100">
                  Next steps for Caredevo
                </h3>
                <ul className="space-y-1 text-[11px] text-slate-400">
                  <li>• Move the widget script to a CDN (e.g. cdn.caredevo.com).</li>
                  <li>• Connect script calls to your Node/Azure backend.</li>
                  <li>• Tie <code className="bg-slate-950 px-1 py-0.5 rounded">data-key</code> to member accounts in MongoDB.</li>
                  <li>• Show this snippet in the Caredevo member dashboard.</li>
                  <li>• Add usage analytics & Stripe metered billing.</li>
                </ul>
              </div>
            </aside>
          </div>
        </main>
      </div>
    );
  }
}

export default VoiceWidget;
